/**
 * MapPlaceLabel Component
 * Renders a place marker with optional label, heart icon, and link functionality
 */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHover, useWrappedTextWidth } from '../MapLabels/MapLabelsHooks';
import { HeartIcon } from '../MapLabels/MapLabelIcons';
import { useMapStateStore, useAppStore } from '../../../stores';
import './MapPlaceLabel.scss';

/**
 * Map Place Label Component
 * Memoized to prevent re-renders when props haven't changed
 */
const MapPlaceLabel = React.memo(({
  place,
  position,
  isLink,
  labelVisible,
  globalLabelVisible = true,
  onToggleLabel,
  onMarkerClick,
  uniqueId,
  isFavorited,
  showHeartEnabled,
  isPulsing = false,
}) => {
  const [isHovered, hoverHandlers] = useHover();
  const [isFocusSvg, setIsFocusSvg] = useState(false);

  const pin = place?.pin || {};
  const {
    svg_id: svgId,
    label_text: labelText = '',
    label_orientation: orientation = 'right',
    unpublished: isUnpublished = false,
    wrap_label: wrapLabel = true,
  } = pin || {};

  // Mark drafts, but only once the server has actually granted preview. The
  // flag is truthful on every response, so gating on previewGranted is what
  // keeps the badge off the public map if an unpublished place ever reaches it.
  const previewGranted = useAppStore((state) => state.previewGranted);
  const showDraftBadge = previewGranted === true && isUnpublished;

  const { labelRef, textRef, fittedWidth } = useWrappedTextWidth(labelText, wrapLabel);
  const svgFocusId = useMapStateStore((state) => state.svgFocusId);

  useEffect(() => {
    if (svgFocusId && svgId && (svgFocusId === svgId)) {
      setIsFocusSvg(svgId);
    } else {
      setIsFocusSvg(false);
    }
  }, [svgFocusId, svgId]);

  const showHeart = showHeartEnabled && isFavorited;

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (isLink) {
      onMarkerClick?.(place);
    } else {
      onToggleLabel?.(uniqueId);
    }
  }, [isLink, place, onMarkerClick, onToggleLabel, uniqueId]);

  // Hide entire marker when global toggle is off (must be after all hooks)
  if (!globalLabelVisible) {
    return null;
  }

  const labelStyle = fittedWidth ? { width: `${fittedWidth}px` } : undefined;

  const markerClasses = classNames('htmlPlaceMarker', {
    'htmlPlaceMarker--hovered': isHovered,
    'htmlPlaceMarker--pulsing': !isLink,
    'htmlPlaceMarker--highlighted': isFocusSvg,
  });

  const labelClasses = classNames(
    'htmlPlaceLabel',
    `htmlPlaceLabel--${orientation}`,
    {
      'htmlPlaceLabel--visible': labelVisible,
      'htmlPlaceLabel--hidden': !labelVisible,
      'htmlPlaceLabel--hovered': isHovered,
      'htmlPlaceLabel--link': isLink,
      'htmlPlaceLabel--withHeart': showHeart,
      'htmlPlaceLabel--pulsing': isPulsing,
      'htmlPlaceLabel--noWrap': !wrapLabel,
    },
  );

  return (
    // A map marker is a positioned dot on an SVG, not a control in the tab order.
    // Giving every marker role=button + tabIndex would rewrite tab order across the
    // whole map, which is a real UX change and out of scope here.
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
       jsx-a11y/no-static-element-interactions */
    <div
      className={markerClasses}
      style={{ left: `${position.x}%`, top: `${position.y}%` }}
      onClick={handleClick}
      onMouseEnter={hoverHandlers.onMouseEnter}
      onMouseLeave={hoverHandlers.onMouseLeave}
    >
      <div className="htmlPlaceMarker__dot">
        {!isLink && <span className="htmlPlaceMarker__plusIcon" />}
      </div>

      {labelText && (isLink || labelVisible) && (
        <div className={labelClasses} ref={labelRef} style={labelStyle}>
          {showHeart && <HeartIcon className="htmlPlaceLabel__heart" isActive />}
          <span className="htmlPlaceLabel__text" ref={textRef}>{labelText}</span>
          {showDraftBadge && (
            <span className="htmlPlaceLabel__draft" data-testid="map-place-draft">
              Draft
            </span>
          )}
          {isLink && (
            <>
              <span className="htmlPlaceLabel__divider" />
              <span className="htmlPlaceLabel__plus" />
            </>
          )}
        </div>
      )}
    </div>
  );
});

MapPlaceLabel.propTypes = {
  place: PropTypes.shape({
    pin: PropTypes.shape({
      svg_id: PropTypes.string,
      label_text: PropTypes.string,
      label_orientation: PropTypes.string,
      unpublished: PropTypes.bool,
      wrap_label: PropTypes.bool,
    }),
  }),
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }).isRequired,
  isLink: PropTypes.bool,
  labelVisible: PropTypes.bool,
  globalLabelVisible: PropTypes.bool,
  onToggleLabel: PropTypes.func,
  onMarkerClick: PropTypes.func,
  uniqueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFavorited: PropTypes.bool,
  showHeartEnabled: PropTypes.bool,
  isPulsing: PropTypes.bool,
};

MapPlaceLabel.defaultProps = {
  place: null,
  isLink: false,
  labelVisible: false,
  globalLabelVisible: true,
  onToggleLabel: undefined,
  onMarkerClick: undefined,
  uniqueId: undefined,
  isFavorited: false,
  showHeartEnabled: false,
  isPulsing: false,
};

export default MapPlaceLabel;
